import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'

import { cn } from '@/lib/utils'
import { CheckIcon, MinusIcon } from 'lucide-react'

interface CheckboxProps extends CheckboxPrimitive.Root.Props {
  size?: 'md' | 'lg'
}

function Checkbox({ className, size = 'md', indeterminate, ...props }: CheckboxProps) {
  const iconSize = size === 'md' ? 'size-4' : 'size-6'

  return (
    <CheckboxPrimitive.Root
      data-slot="checkbox"
      indeterminate={indeterminate}
      className={cn(
        'group/cb relative flex shrink-0 cursor-pointer items-center justify-center border outline-none transition-colors',
        // Size
        size === 'md' ? 'size-5 rounded-100' : 'size-7 rounded-200',
        // Unchecked — default
        'bg-creation-neutral-container-surface-default border-creation-neutral-container-on-surface-default',
        // Unchecked — hover
        'data-[unchecked]:hover:bg-creation-primary-container-surface-hover data-[unchecked]:hover:border-creation-primary-container-on-surface-hover',
        // Unchecked — pressed
        'data-[unchecked]:active:bg-creation-primary-container-surface-pressed data-[unchecked]:active:border-creation-primary-container-on-surface-pressed',
        // Unchecked — focused
        'data-[unchecked]:focus-visible:border-creation-primary-container-on-surface-focused data-[unchecked]:focus-visible:shadow-[0_0_8px_4px_var(--color-primary-a30)]',
        // Unchecked — disabled
        'data-[unchecked]:data-[disabled]:bg-creation-neutral-container-surface-disable data-[unchecked]:data-[disabled]:border-creation-neutral-container-on-surface-disable',
        // Checked — default
        'data-[checked]:border-0 data-[checked]:bg-creation-primary-solid-surface-default',
        // Checked — hover / pressed / focused / disabled
        'data-[checked]:hover:bg-creation-primary-solid-surface-hover',
        'data-[checked]:active:bg-creation-primary-solid-surface-pressed',
        'data-[checked]:focus-visible:bg-creation-primary-solid-surface-focused data-[checked]:focus-visible:shadow-[0_0_8px_4px_var(--color-primary-a30)]',
        'data-[checked]:data-[disabled]:bg-creation-primary-solid-surface-disable',
        // Indeterminate — default
        'data-indeterminate:border-0 data-indeterminate:bg-creation-primary-solid-surface-default',
        // Indeterminate — hover / pressed / focused / disabled
        'data-indeterminate:hover:bg-creation-primary-solid-surface-hover',
        'data-indeterminate:active:bg-creation-primary-solid-surface-pressed',
        'data-indeterminate:focus-visible:bg-creation-primary-solid-surface-focused data-indeterminate:focus-visible:shadow-[0_0_8px_4px_var(--color-primary-a30)]',
        'data-[indeterminate]:data-[disabled]:bg-creation-primary-solid-surface-disable',
        // Disabled cursor
        'data-[disabled]:cursor-not-allowed',
        className,
      )}
      {...props}
    >
      {/* Faint check icon shown on unchecked hover/pressed */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute flex items-center justify-center opacity-0 transition-opacity',
          'text-creation-primary-container-on-surface-hover',
          'group-data-[unchecked]/cb:group-hover/cb:opacity-100',
          'group-data-[unchecked]/cb:group-active/cb:opacity-100 group-data-[unchecked]/cb:group-active/cb:text-creation-primary-container-on-surface-pressed',
          'group-data-[disabled]/cb:!opacity-0',
          iconSize,
        )}
      >
        <CheckIcon className="size-full" />
      </span>

      <CheckboxPrimitive.Indicator
        className={cn(
          'flex items-center justify-center text-white transition-none',
          'group-data-[disabled]/cb:text-creation-primary-solid-on-surface-disable',
          iconSize,
        )}
      >
        {indeterminate ? <MinusIcon className="size-full" /> : <CheckIcon className="size-full" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox }
